/**
 * Phase 58.1 — Billing anchor helpers for Add Plan (ANCHOR-01/02/03).
 *
 * Wave 0 (58.1-01) lands these as REAL exported, typed signatures with bodies
 * that throw — nothing imports them yet, so nothing breaks. Plan 58.1-02
 * fills the bodies and wires them into `add-plan.tsx`, replacing the
 * hard-derive at `add-plan.tsx:540`/`:591`.
 *
 * See: .planning/phases/58.1-billing-anchor-day-on-add-plan-with-prorated-first-period-and-cross-deal-combined-receipt-by-period-start/58.1-CONTEXT.md §Anchor control
 */

/**
 * Day-of-month of a YYYY-MM-DD string, clamped to the 1..31 billing anchor
 * range. Was 1..28 — that stored billing day 28 for every plan starting on the
 * 29th–31st and gave it a short prorated first cycle (e.g. 30 Sep → 27 Oct).
 * Both engines accept 1..31 since 86841b2f and collapse short months to the
 * month's last day (31 in April → 30, 30 in Feb → 28/29).
 */
export function billingDayOf(isoDate: string): number {
  const d = Number(isoDate.slice(8, 10));
  return Math.min(31, Math.max(1, d || 1));
}

/**
 * ANCHOR-03. The anchor an Add Plan line should carry after its start date
 * changes. `touched` = the operator has explicitly set this line's anchor.
 * Untouched lines keep auto-deriving (today's behaviour, byte-identical);
 * touched lines are preserved (the forced re-derive at `add-plan.tsx:591`
 * must stop firing once `touched` is true).
 */
export function nextBillingDayOnStartChange(args: {
  startDate: string;
  currentBillingDay: number;
  touched: boolean;
}): number {
  if (args.touched) {
    return Math.min(31, Math.max(1, args.currentBillingDay || 1));
  }
  return billingDayOf(args.startDate);
}

/**
 * ANCHOR-02. The account's single common recurring billing anchor, or null.
 * Considers only memberships with status 'active' AND billingCycleDay != null
 * (the server nulls billingCycleDay for One_Time and non-recurring lines, so
 * this filter IS the recurring filter). Returns null when the set is empty
 * or the members disagree.
 */
export function commonAccountAnchor(
  memberships: readonly { status?: string | null; billingCycleDay?: number | null }[],
): number | null {
  const days = new Set<number>();
  for (const m of memberships) {
    if (m.status === "active" && m.billingCycleDay != null) {
      days.add(m.billingCycleDay);
    }
  }
  if (days.size !== 1) return null;
  return [...days][0]!;
}
